<script setup lang="ts">
import {
  useCarrierHoursQuery,
  useHighlightedPromoCoupon,
  useProductQuickViewQuery,
  useToggleWishlistMutation,
  useWishlistQuery,
} from "~~/app/composables/queries/useStoreApi";
import { useAddToCartFlow } from "~~/app/composables/useAddToCartFlow";
import { useQuickView } from "~~/app/composables/useQuickView";
import { useNotifications } from "~~/app/composables/useNotifications";
import { useAnalytics } from "~~/app/composables/useAnalytics";
import { withCurrentVariantMarked } from "~~/app/utils/sizeVariants";
import {
  isSizeGroup,
  resolveQuoteVariant,
  resolveVariantSelection,
  type VariantPicks,
} from "~~/app/utils/quoteVariant";
import { pickedColorIds, variantGallery } from "~~/app/utils/variantGallery";
import { variantProductName } from "~~/app/utils/variantDetails";

const { activeSlug, isOpen, close } = useQuickView();
const { formatPrice } = useMoney();
// „+ VAT 23%" przy cenie (BrandConfig.priceTaxNote) — jak na karcie produktu.
const priceTaxNote = useBrand().priceTaxNote ?? "";
// Kółko koloru przy nazwie — jak na karcie produktu (BrandConfig.colorSwatches).
const showColorSwatches = useBrand().colorSwatches === true;
// Galeria wybranego koloru — jak na karcie produktu (BrandConfig.pdpGalleryFollowsColor).
const galleryFollowsColor = useBrand().pdpGalleryFollowsColor === true;
// Nazwa wybranego wariantu — jak na karcie produktu (BrandConfig.pdpDetailsFollowVariant).
const detailsFollowVariant = useBrand().pdpDetailsFollowVariant === true;
const addToCartFlow = useAddToCartFlow();
// Tryb katalogowy: szybki podgląd też prowadzi do wyceny, nie do koszyka.
const { sellingEnabled, ctaLabel } = useCatalogOnly();
const { open: openQuote } = useQuoteRequest();

// Zapytanie otwieramy PO zamknięciu podglądu — dwa nałożone modale to pułapka
// dla klawiatury i czytnika ekranu. W trybie katalogowym niesie wybrany wariant:
// bez niego z karuzeli strony głównej szło zapytanie o „produkt w ogóle"
// i nie było wiadomo, jaki rozmiar wycenić.
function askForQuote() {
  const variant = quoteVariant.value;
  const context = {
    idProduct: product.value?.id ?? null,
    idProductAttribute: variant?.combination.idProductAttribute ?? null,
    name: displayName.value || undefined,
    attributes: variant?.label,
    reference: quoteSku.value || undefined,
  };
  close();
  openQuote(context);
}
const toggleWishlist = useToggleWishlistMutation();
const { data: wishlist } = useWishlistQuery();
const notifications = useNotifications();
const analytics = useAnalytics();

const localSlug = ref<string | null>(null);
const activeThumbIndex = ref(0);
const localSelectedSize = ref("");

// Tryb katalogowy: jawnie wybrane wartości grup atrybutów innych niż rozmiar
// (np. kolor). Rozmiar zostaje przy przyciskach rozmiaru — tych samych co dotąd.
const userVariantPicks = ref<VariantPicks>({});

watch(
  activeSlug,
  (slug) => {
    localSlug.value = slug;
    activeThumbIndex.value = 0;
    localSelectedSize.value = "";
    userVariantPicks.value = {};
  },
  { immediate: true },
);

const { data, isPending } = useProductQuickViewQuery(localSlug);
const product = computed(() => {
  const raw = data.value?.product ?? null;
  if (!raw) return null;
  // Ten sam wzorzec co na PDP — `is-active` w selektorze rozmiaru czyta wyłącznie
  // `isCurrent`, więc gdy backend nie oznaczy wariantu, odtwarzamy flagę po id.
  return {
    ...raw,
    sizeVariants: withCurrentVariantMarked(raw.sizeVariants, raw.id),
  };
});

// Termin dostawy („U Ciebie za 2-3 dni") to obietnica sprzedaży — przy wycenie
// w sklepie katalogowym nie ma racji bytu (tak samo jak na karcie produktu).
const { data: carrierHours } = useCarrierHoursQuery({ enabled: sellingEnabled });
const carrierHoursText = computed(() =>
  sellingEnabled ? (carrierHours.value?.text?.trim() ?? "") : "",
);

// Promo notices (kod rabatowy + countdown) — te same, które wyświetla strona
// produktu. highlightedCoupon pochodzi z koszyka (vue-query), więc bywa
// klient-late, ale jest niezależny od produktu w modalu.
// Stałe kody akcji progowej (lavmag EXTRA*, youlook MORE*) odsiewamy tak samo jak
// na karcie produktu: mają odległą datę końca, więc countdown pokazywałby „1824d".
const highlightedCouponRaw = useHighlightedPromoCoupon();
const { isStandingCode } = useStandingPromo();
const highlightedCoupon = computed(() =>
  isStandingCode(highlightedCouponRaw.value?.code) ? null : highlightedCouponRaw.value,
);
const noticesVisible = computed(
  () => !!(highlightedCoupon.value || product.value?.promoEndDate),
);

const galleryImages = computed(() => {
  if (!product.value) return [];
  const items = product.value.galleryItems;
  const all =
    galleryFollowsColor && items?.length
      ? variantGallery(
          items,
          pickedColorIds(product.value.combinationOptions, variantPicks.value),
        )
      : [product.value.thumbnail, ...product.value.gallery];
  return [...new Set(all.filter(Boolean))];
});
function goToImage(delta: number) {
  const total = galleryImages.value.length;
  if (total <= 1) return;
  activeThumbIndex.value = (activeThumbIndex.value + delta + total) % total;
}

// Mobile: swipe galerii (oprócz strzałek) — gest poziomy > 40px zmienia zdjęcie.
const touchStartX = ref(0);
const touchStartY = ref(0);
function onGalleryTouchStart(e: TouchEvent) {
  const t = e.changedTouches[0];
  if (!t) return;
  touchStartX.value = t.screenX;
  touchStartY.value = t.screenY;
}
function onGalleryTouchEnd(e: TouchEvent) {
  const t = e.changedTouches[0];
  if (!t) return;
  const dx = t.screenX - touchStartX.value;
  const dy = t.screenY - touchStartY.value;
  // Tylko gest poziomy (żeby nie kolidować z pionowym scrollem modala).
  if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
    goToImage(dx < 0 ? 1 : -1);
  }
}

const productLink = computed(() =>
  localSlug.value ? `/products/${localSlug.value}` : "/",
);

// Link do listingu marki — ten sam wzorzec co na PDP (`/marka/{id}-{slug}`).
const brandLink = computed(() =>
  product.value?.brand && product.value?.brandId && product.value?.brandSlug
    ? `/marka/${product.value.brandId}-${product.value.brandSlug}`
    : null,
);

const isFavorite = computed(
  () => wishlist.value?.productIds?.includes(product.value?.id ?? "") || false,
);

const discountPercent = computed(() => {
  if (!product.value?.compareAtPrice || !product.value.price) return null;
  const pct = Math.round(
    (1 - product.value.price / product.value.compareAtPrice) * 100,
  );
  return pct > 0 ? `-${pct}%` : null;
});

const isSoldOut = computed(
  () =>
    !!product.value &&
    product.value.quantity <= 0 &&
    !product.value.allowOutOfStock,
);

const stockIndicator = computed(() => {
  if (!product.value) return null;
  const q = product.value.quantity;
  if (product.value.allowOutOfStock && q <= 0)
    return { label: "Na zamówienie", tone: "yellow" };
  if (q > 2) return { label: `Dostępny (${q} szt.)`, tone: "green" };
  if (q === 2) return { label: "Ostatnie sztuki", tone: "yellow" };
  if (q === 1) return { label: "Ostatnia sztuka", tone: "yellow" };
  return { label: "Niedostępny", tone: "red" };
});

const selectedSize = computed(() => {
  if (!product.value) return "";
  if (product.value.sizeVariants.length > 0) {
    return (
      localSelectedSize.value ||
      (product.value.sizeVariants.find((v) => v.isCurrent)?.rozmiar ?? "")
    );
  }
  return localSelectedSize.value || product.value.defaultSize;
});

function loadVariant(v: { id: string; slug: string }) {
  activeThumbIndex.value = 0;
  localSelectedSize.value = "";
  userVariantPicks.value = {};
  localSlug.value = `${v.id}-${v.slug}`;
}

/**
 * Wariant do wyceny — wyłącznie w trybie katalogowym (sklepy sprzedażowe mają
 * własny wybór pod koszyk, nietknięty). Produkt bez kombinacji nie ma wariantu.
 */
const hasQuoteVariants = computed(
  () =>
    !sellingEnabled &&
    !!product.value &&
    product.value.combinationOptions.length > 0 &&
    product.value.combinations.length > 0,
);

/**
 * Grupy z jawnym wyborem w podglądzie: wszystkie poza rozmiarem, a rozmiar też,
 * gdy produkt nie ma przycisków rozmiaru (inaczej nie dałoby się go wybrać).
 */
const quoteVariantGroups = computed(() => {
  if (!hasQuoteVariants.value || !product.value) return [];
  const hasSizeButtons =
    product.value.sizeVariants.length > 0 || product.value.sizes.length > 0;
  return product.value.combinationOptions.filter(
    (group) => !isSizeGroup(group) || !hasSizeButtons,
  );
});

// Przyciski tylko z wartościami istniejącymi w wybranym rozmiarze. Wybrany
// kolor, którego nie ma w nowo klikniętym rozmiarze, ustępuje domyślnemu,
// a etykieta mówi wprost, jaki wariant idzie do wyceny.
const variantSelection = computed(() =>
  hasQuoteVariants.value && product.value
    ? resolveVariantSelection(
        product.value.combinationOptions,
        product.value.combinations,
        {
          sizeLabel: selectedSize.value,
          shown: quoteVariantGroups.value,
          preferred: userVariantPicks.value,
        },
      )
    : { picks: {}, groups: [] },
);
const variantPicks = computed<VariantPicks>(() => variantSelection.value.picks);

// Inny kolor = inny zestaw zdjęć → od pierwszego (indeks z poprzedniego
// zestawu wskazywałby przypadkowe zdjęcie albo nic). Po `variantPicks` —
// watch od razu liczy galerię, a ta czyta wybór.
if (galleryFollowsColor) {
  watch(
    () => galleryImages.value.join("|"),
    () => {
      activeThumbIndex.value = 0;
    },
  );
}

// Pozycja listy zapytania: pierwsze zdjęcie wybranego koloru (jak na karcie),
// bez flagi okładka produktu jak dotąd.
const quoteListImage = computed(
  () =>
    (galleryFollowsColor ? galleryImages.value[0] : undefined) ||
    product.value?.thumbnail ||
    null,
);

const quoteVariant = computed(() =>
  hasQuoteVariants.value && product.value
    ? resolveQuoteVariant(
        product.value.combinationOptions,
        product.value.combinations,
        variantPicks.value,
      )
    : undefined,
);

/**
 * SKU wybranego wariantu: `reference` kombinacji z API. Gdy go brak (backend
 * sprzed pola albo kombinacja bez własnego SKU), SKU produktu pokazujemy tylko
 * przy kombinacji DOMYŚLNEJ — to jej SKU (QA: „S16000-SK-XXL" = XXL/Blekitny),
 * a pod innym rozmiarem byłoby nieprawdą. Backend i tak zapisuje w wątku SKU
 * wariantu z bazy.
 */
const quoteSku = computed(() => {
  if (sellingEnabled || !product.value) return "";
  if (!hasQuoteVariants.value) return product.value.sku;
  const variant = quoteVariant.value;
  if (variant?.combination.reference) return variant.combination.reference;
  return variant?.isDefault ? product.value.sku : "";
});

// Nazwa z EasyItem niesie atrybuty kombinacji domyślnej („BOSTON Blekitny”) —
// przy innym wyborze podmieniamy je na wybrane (app/utils/variantDetails).
const displayName = computed(() => {
  const current = product.value;
  if (!current) return "";
  if (!detailsFollowVariant || !hasQuoteVariants.value) return current.name;
  return variantProductName(
    current.name,
    current.combinationOptions,
    variantPicks.value,
  );
});

function pickVariantValue(groupName: string, id: number) {
  userVariantPicks.value = { ...userVariantPicks.value, [groupName]: id };
}

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

function resolveAddToCartTarget() {
  if (!product.value) return null;
  const productId = Number(product.value.id);
  const selected = (
    selectedSize.value ||
    product.value.defaultSize ||
    ""
  ).trim();

  if ((product.value.sizeVariants?.length ?? 0) > 0) {
    const match = product.value.sizeVariants.find(
      (v) => normalize(v.rozmiar) === normalize(selected),
    );
    if (!match) return null;
    const id = Number(match.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    return { productId: id, productAttributeId: 0 };
  }

  if (
    (product.value.combinationOptions?.length ?? 0) > 0 &&
    (product.value.combinations?.length ?? 0) > 0
  ) {
    const sizeGroup = product.value.combinationOptions.find(
      (g) => normalize(g.name) === "rozmiar",
    );
    const item = sizeGroup?.items.find(
      (i) => normalize(i.value) === normalize(selected),
    );
    if (!item) return null;
    const combo = product.value.combinations.find(
      (c) => normalize(c.combinationCode) === normalize(String(item.id)),
    );
    if (
      !combo ||
      !Number.isFinite(combo.idProductAttribute) ||
      combo.idProductAttribute <= 0
    )
      return null;
    return {
      productId: Number.isFinite(productId) ? productId : 0,
      productAttributeId: combo.idProductAttribute,
    };
  }

  return {
    productId: Number.isFinite(productId) ? productId : 0,
    productAttributeId: 0,
  };
}

const atStockLimit = computed(() => {
  const target = resolveAddToCartTarget();
  if (!target) return false;
  return addToCartFlow.isAtStockLimit?.(target) ?? false;
});

// Potwierdzenie dodania do koszyka — czytelny komunikat „✓ Dodano" zamiast
// mylącego czerwonego „Więcej nie mamy…" (które pojawia się przy ostatniej sztuce
// PO udanym dodaniu). Znika po chwili.
const addedFlash = ref(false);
let addedFlashTimer: ReturnType<typeof setTimeout> | null = null;
function flashAdded() {
  addedFlash.value = true;
  if (addedFlashTimer) clearTimeout(addedFlashTimer);
  addedFlashTimer = setTimeout(() => {
    addedFlash.value = false;
  }, 2500);
}

async function addToCart() {
  const target = resolveAddToCartTarget();
  if (!target) return;
  const currentProduct = product.value;
  try {
    await addToCartFlow.add({
      productId: target.productId,
      productAttributeId: target.productAttributeId,
      quantity: 1,
    });
    if (addToCartFlow.stockError.value) {
      notifications.error(addToCartFlow.stockError.value);
    } else if (currentProduct) {
      flashAdded();
      analytics.trackAddToCart(currentProduct, 1, {
        variant: localSelectedSize.value || undefined,
      });
    }
  } catch {
    // errors handled by addToCartFlow notifications
  }
}

function toggleFavorite() {
  if (!product.value) return;
  const wasFavorite = isFavorite.value;
  const currentProduct = product.value;
  toggleWishlist.mutate(
    { productId: currentProduct.id, isFavorite: wasFavorite },
    {
      onSuccess: () => {
        if (wasFavorite) analytics.trackRemoveFromWishlist(currentProduct);
        else analytics.trackAddToWishlist(currentProduct);
      },
    },
  );
}

const sanitizedDescription = computed(() => {
  const value = product.value?.description ?? "";
  const stripped = value
    .replace(/<\s*br\s*\/?\s*>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/\n{3,}/g, "\n\n");
  return (
    stripped
      .replace(/&nbsp;/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'")
      .trim()
      // Treść z PrestaShop często zaczyna się od własnego nagłówka "OPIS" /
      // "OPIS PRODUKTU", który dubluje tytuł sekcji opisu — usuń go.
      .replace(/^\s*opis(\s+produktu)?\s*\n+/i, "")
      .trim()
  );
});

function handleKeydown(e: KeyboardEvent) {
  if (!isOpen.value) return;
  if (e.key === "Escape") close();
  else if (e.key === "ArrowLeft") goToImage(-1);
  else if (e.key === "ArrowRight") goToImage(1);
}

watch(isOpen, (open) => {
  if (!import.meta.client) return;
  document.body.style.overflow = open ? "hidden" : "";
  if (!open) addedFlash.value = false;
});

onMounted(() => document.addEventListener("keydown", handleKeydown));
onUnmounted(() => {
  document.removeEventListener("keydown", handleKeydown);
  document.body.style.overflow = "";
  if (addedFlashTimer) clearTimeout(addedFlashTimer);
});
</script>

<template>
  <Teleport to="body">
    <Transition name="qv-fade">
      <div v-if="isOpen" class="qv-backdrop" @click.self="close">
        <Transition name="qv-rise" appear>
          <div
            v-if="isOpen"
            class="qv-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Szybki podgląd produktu"
          >
            <!-- Close -->
            <button
              type="button"
              class="qv-close"
              aria-label="Zamknij podgląd"
              @click="close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>

            <!-- Skeleton -->
            <template v-if="isPending || !product">
              <div class="qv-skeleton-gallery">
                <div class="qv-skeleton-main" />
                <div class="qv-skeleton-thumbs">
                  <div v-for="n in 4" :key="n" class="qv-skeleton-thumb" />
                </div>
              </div>
              <div class="qv-skeleton-panel">
                <div class="qv-skel qv-skel--xs" />
                <div class="qv-skel qv-skel--lg" />
                <div class="qv-skel qv-skel--md" style="margin-top: 8px" />
                <div class="qv-skel qv-skel--sm" style="margin-top: 4px" />
                <div class="qv-skel-divider" />
                <div class="qv-skel qv-skel--sm" />
                <div class="qv-skel-chips">
                  <div v-for="n in 5" :key="n" class="qv-skel-chip" />
                </div>
                <div class="qv-skel qv-skel--btn" />
              </div>
            </template>

            <!-- Content -->
            <template v-else>
              <!-- Gallery -->
              <div class="qv-gallery">
                <div
                  class="qv-gallery__main"
                  @touchstart.passive="onGalleryTouchStart"
                  @touchend="onGalleryTouchEnd"
                >
                  <!-- Crossfade warstwowy: wszystkie zdjęcia nałożone, aktywne z opacity 1.
                       Klik strzałki = płynne przejście zamiast przeskoku; zdjęcia doczytane
                       raz przy otwarciu modala → przełączanie jest natychmiastowe. -->
                  <ProductImage
                    v-for="(img, idx) in galleryImages"
                    :key="img"
                    :src="img"
                    :alt="product.name"
                    width="560"
                    height="747"
                    format="webp"
                    class="qv-gallery__img"
                    :class="{ 'is-active': idx === activeThumbIndex }"
                    :aria-hidden="idx === activeThumbIndex ? undefined : 'true'"
                  />
                  <ProductImage
                    v-if="galleryImages.length === 0"
                    :src="null"
                    :alt="product.name"
                    width="560"
                    height="747"
                    class="qv-gallery__img is-active"
                  />
                  <span v-if="discountPercent" class="qv-gallery__discount">{{
                    discountPercent
                  }}</span>

                  <template v-if="galleryImages.length > 1">
                    <button
                      type="button"
                      class="qv-gallery__nav qv-gallery__nav--prev"
                      aria-label="Poprzednie zdjęcie"
                      @click="goToImage(-1)"
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="qv-gallery__nav qv-gallery__nav--next"
                      aria-label="Następne zdjęcie"
                      @click="goToImage(1)"
                    >
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </button>
                  </template>
                </div>

                <div v-if="galleryImages.length > 1" class="qv-gallery__thumbs">
                  <button
                    v-for="(img, idx) in galleryImages.slice(0, 6)"
                    :key="img"
                    type="button"
                    class="qv-gallery__thumb"
                    :class="{ 'is-active': activeThumbIndex === idx }"
                    :aria-label="`Pokaż zdjęcie ${idx + 1}`"
                    @click="activeThumbIndex = idx"
                  >
                    <NuxtImg
                      :src="img"
                      :alt="`${product.name} – ${idx + 1}`"
                      width="80"
                      height="107"
                      format="webp"
                      class="qv-gallery__thumb-img"
                    />
                  </button>
                </div>
              </div>

              <!-- Info panel -->
              <article class="qv-panel">
                <div class="qv-panel__top">
                  <NuxtLink
                    v-if="brandLink"
                    :to="brandLink"
                    no-prefetch
                    class="qv-brand qv-brand--link"
                    :aria-label="`Zobacz wszystkie produkty marki ${product.brand}`"
                    @click="close"
                  >
                    {{ product.brand }}
                  </NuxtLink>
                  <p v-else class="qv-brand">{{ product.brand }}</p>
                  <span v-if="discountPercent" class="qv-sale-badge"
                    >Wyprzedaż</span
                  >
                </div>

                <h2 class="qv-name">{{ displayName }}</h2>

                <div class="qv-price-block">
                  <span
                    class="qv-price"
                    :class="{ 'qv-price--sale': product.compareAtPrice }"
                  >
                    {{ formatPrice(product.price) }}
                  </span>
                  <span v-if="priceTaxNote" class="qv-price-tax">{{ priceTaxNote }}</span>
                  <span v-if="product.compareAtPrice" class="qv-compare">
                    {{ formatPrice(product.compareAtPrice) }}
                  </span>
                </div>

                <p
                  v-if="product.lowestRecentPrice && product.compareAtPrice"
                  class="qv-lowest"
                >
                  {{ formatPrice(product.lowestRecentPrice) }} najniższa cena w
                  ostatnich 30 dniach
                </p>

                <div class="qv-divider" />

                <!-- Sizes -->
                <div class="qv-field">
                  <div class="qv-field-row">
                    <p class="qv-field-label">
                      Rozmiar<template v-if="selectedSize"
                        >:
                        <span class="qv-field-value">{{
                          selectedSize
                        }}</span></template
                      >
                    </p>
                    <p v-if="stockIndicator" class="qv-availability">
                      <span
                        class="qv-availability-dot"
                        :class="`qv-availability-dot--${stockIndicator.tone}`"
                      />
                      {{ stockIndicator.label }}
                    </p>
                  </div>

                  <div v-if="product.sizeVariants.length > 0" class="qv-sizes">
                    <button
                      v-for="v in product.sizeVariants"
                      :key="v.id"
                      type="button"
                      class="qv-size-btn"
                      :class="{
                        'is-active': v.isCurrent,
                        'is-unavailable': !v.available,
                      }"
                      :disabled="!v.available"
                      @click="v.available && loadVariant(v)"
                    >
                      {{ v.rozmiar }}
                    </button>
                  </div>

                  <div v-else-if="product.sizes.length > 0" class="qv-sizes">
                    <button
                      v-for="size in product.sizes"
                      :key="size.label"
                      type="button"
                      class="qv-size-btn"
                      :class="{
                        'is-active': selectedSize === size.label,
                        'is-unavailable': !size.available,
                      }"
                      :disabled="!size.available"
                      @click="
                        size.available && (localSelectedSize = size.label)
                      "
                    >
                      {{ size.label }}
                    </button>
                  </div>
                </div>

                <!-- Tryb katalogowy: pozostałe grupy atrybutów (np. kolor) z jawnym
                     wyborem — do wyceny idzie jedna, konkretna kombinacja. Tylko
                     wartości istniejące w wybranym rozmiarze. -->
                <div
                  v-for="group in variantSelection.groups"
                  :key="group.name"
                  class="qv-field"
                >
                  <p class="qv-field-label">
                    {{ group.name }}<template
                      v-if="group.items.some((i) => i.id === variantPicks[group.name])"
                      >:
                      <span class="qv-field-value">{{
                        group.items.find((i) => i.id === variantPicks[group.name])?.value
                      }}</span></template
                    >
                  </p>
                  <div class="qv-sizes">
                    <button
                      v-for="item in group.items"
                      :key="item.id"
                      type="button"
                      class="qv-size-btn"
                      :class="{
                        'is-active': variantPicks[group.name] === item.id,
                        'qv-size-btn--swatch': showColorSwatches && group.isColor,
                      }"
                      @click="pickVariantValue(group.name, item.id)"
                    >
                      <LazyProductColorSwatch
                        v-if="showColorSwatches && group.isColor"
                        :name="item.value"
                        :hex="item.hex"
                      />
                      {{ item.value }}
                    </button>
                  </div>
                </div>

                <!-- Tryb katalogowy: co dokładnie idzie do wyceny — wariant i SKU.
                     Wcześniej podgląd nie pokazywał ani jednego, ani drugiego. -->
                <div
                  v-if="!sellingEnabled && (quoteVariant || quoteSku)"
                  class="qv-quote-variant"
                >
                  <p v-if="quoteVariant" class="qv-variant">
                    Wariant do wyceny: <strong>{{ quoteVariant.label }}</strong>
                  </p>
                  <p v-if="quoteSku" class="qv-sku">SKU: {{ quoteSku }}</p>
                </div>

                <!-- CTA -->
                <div class="qv-cta">
                  <!-- Przycisk, nie NuxtLink: `@click.prevent` nie zatrzymuje
                       nawigacji NuxtLinka, więc zamiast modalu wyceny klient
                       lądował na /contact. -->
                  <button
                    v-if="!sellingEnabled"
                    type="button"
                    class="qv-add-to-cart"
                    @click="askForQuote"
                  >
                    {{ ctaLabel }}
                  </button>
                  <button
                    v-else-if="!isSoldOut"
                    type="button"
                    class="qv-add-to-cart"
                    :class="{ 'is-added': addedFlash }"
                    :disabled="
                      addToCartFlow.isPending.value ||
                      (atStockLimit && !addedFlash)
                    "
                    @click="addToCart"
                  >
                    {{
                      addedFlash ? "✓ Dodano do koszyka" : "Dodaj do koszyka"
                    }}
                  </button>
                  <p v-else class="qv-sold-out">Produkt niedostępny</p>
                  <!-- Ulubione wymagają konta — w sklepie katalogowym ich nie ma. -->
                  <button
                    v-if="sellingEnabled"
                    type="button"
                    class="qv-wishlist-btn"
                    :class="{ 'is-favorite': isFavorite }"
                    :aria-label="
                      isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'
                    "
                    @click="toggleFavorite"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      :fill="isFavorite ? 'currentColor' : 'none'"
                      stroke="currentColor"
                      stroke-width="1.5"
                    >
                      <path
                        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                      />
                    </svg>
                  </button>
                </div>

                <!-- Lista zapytania także z podglądu — z tym samym wariantem co
                     „Zapytaj o wycenę". Produkt z kombinacjami bez rozstrzygniętego
                     wariantu nie trafia na listę jako „produkt w ogóle". -->
                <QuoteListButton
                  v-if="!sellingEnabled && (!hasQuoteVariants || quoteVariant)"
                  class="qv-quote-list"
                  :id-product="product.id"
                  :id-product-attribute="quoteVariant?.combination.idProductAttribute ?? 0"
                  :attributes="quoteVariant?.label ?? ''"
                  :name="product.name"
                  :reference="quoteSku"
                  :image="quoteListImage"
                  :link="productLink"
                />

                <p v-if="addToCartFlow.stockError.value" class="qv-stock-error">
                  Brak wystarczającej ilości w magazynie.
                </p>

                <p v-if="atStockLimit" class="qv-stock-limit">
                  Masz już w koszyku wszystkie dostępne sztuki tego produktu.
                </p>

                <p v-if="carrierHoursText" class="qv-notice">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                  <ColorMarkedText :text="carrierHoursText" />
                </p>

                <!-- Promo notices — kod rabatowy + countdown (jak na stronie produktu) -->
                <div v-if="noticesVisible" class="qv-notices">
                  <ProductPromoCode
                    v-if="highlightedCoupon"
                    :coupon="highlightedCoupon"
                  />
                  <ProductPromoCountdown
                    v-if="highlightedCoupon?.dateTo || product.promoEndDate"
                    :end-date="
                      highlightedCoupon?.dateTo || product.promoEndDate
                    "
                  />
                </div>

                <!-- Description — wypełnia resztę wolnej wysokości panelu -->
                <section
                  v-if="sanitizedDescription"
                  class="qv-description-section"
                >
                  <h3 class="qv-description-heading">Opis produktu</h3>
                  <div class="qv-description-body">
                    <p class="qv-description">{{ sanitizedDescription }}</p>
                  </div>
                </section>

                <NuxtLink :to="productLink" class="qv-full-link" @click="close">
                  Zobacz pełną stronę produktu
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.5"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </NuxtLink>
              </article>

              <!-- Mobile: pływający pasek "Dodaj do koszyka" (jak na karcie/PDP) -->
              <div class="qv-mobile-cta">
                <button
                  v-if="!sellingEnabled"
                  type="button"
                  class="qv-add-to-cart"
                  @click="askForQuote"
                >
                  {{ ctaLabel }}
                </button>
                <button
                  v-else-if="!isSoldOut"
                  type="button"
                  class="qv-add-to-cart"
                  :class="{ 'is-added': addedFlash }"
                  :disabled="
                    addToCartFlow.isPending.value ||
                    (atStockLimit && !addedFlash)
                  "
                  @click="addToCart"
                >
                  {{ addedFlash ? "✓ Dodano do koszyka" : "Dodaj do koszyka" }}
                </button>
                <p v-else class="qv-sold-out">Produkt niedostępny</p>
                <!-- Ulubione wymagają konta — w sklepie katalogowym ich nie ma. -->
                <button
                  v-if="sellingEnabled"
                  type="button"
                  class="qv-wishlist-btn"
                  :class="{ 'is-favorite': isFavorite }"
                  :aria-label="
                    isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'
                  "
                  @click="toggleFavorite"
                >
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    :fill="isFavorite ? 'currentColor' : 'none'"
                    stroke="currentColor"
                    stroke-width="1.5"
                  >
                    <path
                      d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
                    />
                  </svg>
                </button>
              </div>
            </template>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* ── Backdrop ── */
.qv-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 15, 15, 0.56);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  /* Tier modala — ponad SiteHeader (--z-dropdown: 100); 90 sprawiało, że
     nagłówek przeświecał nad backdropem i zasłaniał X oraz strzałki. */
  z-index: var(--z-modal, 400);
  overflow-y: auto;
}

/* ── Dialog ── */
.qv-dialog {
  position: relative;
  background: var(--color-white);
  width: min(1060px, 100%);
  max-height: calc(100dvh - 40px);
  display: grid;
  grid-template-columns: 52% 48%;
  /* Wiersz ograniczony do wysokości dialogu (min 0) — bez tego ścieżka wiersza
     rośnie do treści panelu i przy długim opisie dół panelu (link) ginie pod
     overflow:hidden bez scrolla. Z minmax(0,1fr) panel mieści się w dialogu,
     a nadmiar opisu przewija się w .qv-description-body. */
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
}

/* ── Close button ── */
.qv-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 10;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: 50%;
  cursor: pointer;
  color: var(--color-black);
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.qv-close:hover {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

/* ── Gallery ── */
.qv-gallery {
  display: flex;
  flex-direction: column;
  background: var(--color-bg);
  overflow: hidden;
}

.qv-gallery__main {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  /* Tło pod placeholderem i pod pasami contain (proles); cover odzieży
     wypełnia kadr bez ramek. */
  background: var(--color-white);
}

.qv-gallery__img {
  width: 100%;
  height: 100%;
  /* Kadrowanie z tokenów marki (themes.css). */
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  display: block;
  transition:
    opacity 0.4s ease,
    transform 0.35s ease;
}

/* Warstwy crossfade: aktywne zdjęcie zostaje w przepływie (trzyma wysokość boxa),
   pozostałe nałożone z opacity 0. Wszystkie mają ten sam box 3:4 (contain) → bez
   zmiany layoutu przy przełączaniu. Aktywne nad nieaktywnymi (z-index), pod
   strzałkami (z-index 4) i odznaką promocji (z-index 3). */
.qv-gallery__img:not(.is-active) {
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
}

.qv-gallery__img.is-active {
  position: relative;
  opacity: 1;
  z-index: 1;
}

.qv-gallery__main:hover .qv-gallery__img.is-active {
  transform: scale(1.025);
}

@media (prefers-reduced-motion: reduce) {
  .qv-gallery__img {
    transition: none;
  }
}

.qv-gallery__discount {
  position: absolute;
  top: 14px;
  left: 14px;
  z-index: 3;
  background: var(--color-sale);
  color: var(--color-white);
  font-size: 0.75rem;
  font-weight: var(--font-weight-normal);
  padding: 3px 10px;
  border-radius: 999px;
  pointer-events: none;
}

[data-brand="youlook"] .qv-gallery__discount {
  border-radius: 4px;
}

/* ── Gallery nav arrows ── */
.qv-gallery__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 4;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid var(--color-border-md);
  border-radius: 50%;
  color: var(--color-black);
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.qv-gallery__nav:hover {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

.qv-gallery__nav--prev {
  left: 12px;
}

.qv-gallery__nav--next {
  right: 12px;
}

.qv-gallery__thumbs {
  display: flex;
  gap: 3px;
  padding: 4px;
  background: var(--color-bg);
  flex-shrink: 0;
}

.qv-gallery__thumb {
  flex: 1;
  min-width: 0;
  padding: 0;
  border: 2px solid transparent;
  background: none;
  cursor: pointer;
  overflow: hidden;
  transition: border-color var(--transition-fast);
  /* Kształt brandu (#2): przycisk zaokrąglony razem z obrazem, żeby aktywna
     ramka (.is-active border-color) pasowała do zaokrąglonego zdjęcia. */
  border-radius: var(--product-img-radius);
}

.qv-gallery__thumb.is-active {
  border-color: var(--color-black);
}

.qv-gallery__thumb-img {
  width: 100%;
  aspect-ratio: 0.75;
  object-fit: var(--product-img-fit);
  object-position: var(--product-img-position);
  /* Pasy contain (proles) białe jak w kaflu, nie szare z tła paska miniatur. */
  background: var(--product-img-letterbox-bg);
  display: block;
  transition: opacity var(--transition-fast);
  border-radius: var(--product-img-radius);
}

.qv-gallery__thumb:not(.is-active):hover .qv-gallery__thumb-img {
  opacity: 0.75;
}

/* ── Panel ── */
.qv-panel {
  padding: 36px 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  /* min-height:0 — grid-item musi móc skurczyć się poniżej swojej treści, by
     wewnętrzny scroll opisu (a nie wzrost wiersza grida) wchłaniał nadmiar. */
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.qv-panel__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  /* Rezerwa pod przycisk X (.qv-close: right:16px + width:36px → lewa krawędź na
     52px od prawej krawędzi dialogu). Bez tego badge „Wyprzedaż" przy prawej
     krawędzi treści panelu (padding 36px) wchodzi pod X. 28px wsuwa badge na
     64px od prawej → 12px luzu od przycisku. */
  padding-right: 28px;
}

[data-brand="youlook"] .qv-panel__top {
  justify-content: flex-start;
  align-items: start;
  flex-flow: column-reverse;
}

.qv-brand {
  margin: 0;
  font-size: 0.7rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-muted);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1;
}

.qv-brand--link {
  text-decoration: underline;
}

.qv-brand--link:hover {
  color: var(--color-black);
}

.qv-sale-badge {
  font-size: 0.7rem;
  font-weight: var(--font-weight-normal);
  background: var(--color-sale);
  color: var(--color-white);
  padding: 3px 10px;
  border-radius: 999px;
  line-height: 1;
  white-space: nowrap;
}

[data-brand="youlook"] .qv-sale-badge {
  border-radius: 4px;
}

.qv-name {
  margin: 0;
  font-size: 1.375rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
  text-transform: uppercase;
}

.qv-price-block {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.qv-price {
  font-size: 1.375rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.qv-price--sale {
  color: var(--color-sale);
}

/* Dopisek „+ VAT 23%" (BrandConfig.priceTaxNote) — dopisek, nie druga cena. */
.qv-price-tax {
  font-size: 0.8125rem;
  color: var(--color-muted);
  white-space: nowrap;
}

.qv-compare {
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  text-decoration: line-through;
  line-height: 1;
}

.qv-lowest {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  line-height: 1.4;
}

.qv-divider {
  height: 1px;
  background: var(--color-border);
  flex-shrink: 0;
}

.qv-field {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qv-field-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.qv-field-label {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1;
}

.qv-field-value {
  font-weight: var(--font-weight-light);
}

.qv-availability {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
  white-space: nowrap;
}

.qv-availability-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
  background: var(--color-muted);
}

.qv-availability-dot--green {
  background: #1f7a3f;
}
.qv-availability-dot--yellow {
  background: #8a6500;
}
.qv-availability-dot--red {
  background: #b42318;
}

.qv-sizes {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.qv-size-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 46px;
  height: 38px;
  padding: 0 12px;
  border: 0;
  border-bottom: 2px solid var(--color-border-md);
  background: transparent;
  font-family: var(--font-body);
  font-size: 1rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  cursor: pointer;
  transition: border-color var(--transition-fast);
}

[data-brand="youlook"] .qv-size-btn {
  border-radius: 4px;
  border: 1px solid var(--color-border);
}

.qv-size-btn:hover:not(.is-unavailable) {
  border-color: var(--color-black);
}

.qv-size-btn.is-active {
  border-color: var(--color-black);
  font-weight: var(--font-weight-normal);
}

.qv-size-btn.is-unavailable {
  opacity: 0.28;
  cursor: not-allowed;
}

/* Przycisk koloru z kółkiem (BrandConfig.colorSwatches): długa para nazw
   („Grey Melange/ French Navy”) + kółko zawija się na telefonie — przycisk rośnie
   z treścią zamiast wypuszczać ją poza sztywne 38 px. Inne marki tej klasy nie mają. */
.qv-size-btn--swatch {
  height: auto;
  min-height: 38px;
  padding-block: 4px;
  text-align: start;
}

/* Tryb katalogowy: wariant i SKU, które idą do wyceny. */
.qv-quote-variant {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.qv-variant,
.qv-sku {
  margin: 0;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-muted);
}

.qv-variant strong {
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
}

/* ── CTA ── */
.qv-cta {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

/* Pływający pasek CTA — tylko mobile (reguły w @media 720px). */
.qv-mobile-cta {
  display: none;
}

/* Ten sam token co „Zapytaj o wycenę" na kaflu i karcie produktu — bez niego
   proles miał tu grafit, a obok kafla turkus. Fallback = dotychczasowa czerń. */
.qv-add-to-cart {
  flex: 1;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--btn-primary-bg, var(--color-black));
  color: var(--btn-primary-text, var(--color-white));
  border: 1px solid var(--btn-primary-bg, var(--color-black));
  border-radius: var(--btn-pill-radius);
  font-family: var(--font-body);
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  letter-spacing: 0.04em;
  cursor: pointer;
  transition:
    background var(--transition-fast),
    color var(--transition-fast);
}

.qv-add-to-cart:hover {
  background: var(--color-white);
  color: var(--color-black);
}

.qv-add-to-cart:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Potwierdzenie dodania — zielony, czytelny sygnał sukcesu (#210). */
.qv-add-to-cart.is-added,
.qv-add-to-cart.is-added:hover {
  background: #16a34a;
  border-color: #16a34a;
  color: var(--color-white);
  opacity: 1;
  cursor: default;
}

.qv-sold-out {
  flex: 1;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--color-border-md);
  border-radius: 999px;
  font-size: 0.875rem;
  color: var(--color-muted);
  margin: 0;
}

.qv-wishlist-btn {
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: var(--btn-pill-radius);
  cursor: pointer;
  color: var(--color-black);
  transition:
    background var(--transition-fast),
    color var(--transition-fast),
    border-color var(--transition-fast);
}

.qv-wishlist-btn:hover {
  background: var(--color-black);
  color: var(--color-white);
  border-color: var(--color-black);
}

.qv-wishlist-btn.is-favorite {
  color: var(--color-sale);
}

.qv-stock-error {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--color-sale);
  font-weight: var(--font-weight-light);
}

.qv-stock-limit {
  margin: 4px 0 0;
  font-size: 0.8125rem;
  /* Informacyjne, nie alarmowe — produkt dodał się, to tylko limit stanu. */
  color: var(--color-muted);
  font-weight: var(--font-weight-light);
}

.qv-notice {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 9px 14px;
  background: var(--color-bg);
  border-radius: 999px;
  font-size: 0.8125rem;
  color: var(--color-black);
  line-height: 1;
}

[data-brand="youlook"] .qv-notice {
  background: transparent;
}

.qv-notice strong {
  font-weight: 700;
}

/* Stos promo-notices (kod rabatowy + countdown) — własne style pill mają
   komponenty potomne, tu tylko rytm 8px jak w .pdp__notices na PDP. */
.qv-notices {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.qv-full-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: auto;
  font-size: 0.8125rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  text-decoration: underline;
  transition: opacity var(--transition-fast);
}

.qv-full-link:hover {
  opacity: 0.6;
}

/* ── Skeleton ── */
@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.qv-skeleton-gallery {
  background: var(--color-bg);
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
}

.qv-skeleton-main {
  flex: 1;
  background: linear-gradient(
    100deg,
    var(--color-bg) 0%,
    var(--color-border) 38%,
    var(--color-white) 50%,
    var(--color-border) 62%,
    var(--color-bg) 100%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.qv-skeleton-thumbs {
  display: flex;
  gap: 3px;
}

.qv-skeleton-thumb {
  flex: 1;
  aspect-ratio: 0.75;
  background: linear-gradient(
    100deg,
    var(--color-bg) 0%,
    var(--color-border) 38%,
    var(--color-white) 50%,
    var(--color-border) 62%,
    var(--color-bg) 100%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

.qv-skeleton-panel {
  padding: 36px 36px 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.qv-skel {
  border-radius: 4px;
  background: linear-gradient(
    100deg,
    var(--color-bg) 0%,
    var(--color-border) 38%,
    var(--color-white) 50%,
    var(--color-border) 62%,
    var(--color-bg) 100%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
  height: 14px;
}

.qv-skel--xs {
  width: 30%;
}
.qv-skel--sm {
  width: 45%;
}
.qv-skel--md {
  width: 65%;
}
.qv-skel--lg {
  width: 85%;
  height: 22px;
}
.qv-skel--btn {
  height: 52px;
  border-radius: 999px;
  width: 100%;
  margin-top: 8px;
}

.qv-skel-divider {
  height: 1px;
  background: var(--color-border);
  margin: 4px 0;
}

.qv-skel-chips {
  display: flex;
  gap: 8px;
}

.qv-skel-chip {
  width: 48px;
  height: 38px;
  border-radius: 4px;
  background: linear-gradient(
    100deg,
    var(--color-bg) 0%,
    var(--color-border) 38%,
    var(--color-white) 50%,
    var(--color-border) 62%,
    var(--color-bg) 100%
  );
  background-size: 220% 100%;
  animation: shimmer 1.4s ease-in-out infinite;
}

/* ── Transitions ── */
.qv-fade-enter-from,
.qv-fade-leave-to {
  opacity: 0;
}

.qv-fade-enter-active {
  transition: opacity 220ms ease;
}

.qv-fade-leave-active {
  transition: opacity 180ms ease;
}

.qv-rise-enter-from {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}

.qv-rise-enter-active {
  transition:
    opacity 260ms ease,
    transform 260ms cubic-bezier(0.16, 1, 0.3, 1);
}

.qv-rise-leave-active {
  transition:
    opacity 160ms ease,
    transform 160ms ease;
}

.qv-rise-leave-to {
  opacity: 0;
  transform: translateY(6px) scale(0.99);
}

/* ── Description ── */
/* Sekcja flex:1 — wypełnia resztę wolnej wysokości panelu: przy krótkim opisie
   rozciąga się i dosuwa link do dołu, przy długim oddaje nadmiar do wewnętrznego
   scrolla w .qv-description-body (dzięki min-height:0 w całym łańcuchu), więc
   CTA i link zostają widoczne, a dialog nie wychodzi poza viewport. */
.qv-description-section {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-top: 1px solid var(--color-border);
  padding-top: 16px;
}

.qv-description-heading {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.2;
}

.qv-description-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.qv-description {
  margin: 0;
  font-size: 0.875rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.7;
  white-space: pre-line;
}

@media (max-width: 720px) {
  .qv-dialog {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    max-height: none;
    width: 100%;
  }

  /* Editorial: box 3:4 (mediana źródła 0.75–0.78) + contain → cały model bez kadru.
     187/300 (0.623) ucinało boki sylwetki na zdjęciach 0.75–0.78. */
  .qv-gallery {
    max-height: none;
  }

  .qv-gallery__main {
    flex: none;
    width: 100%;
    aspect-ratio: 0.75;
  }

  .qv-gallery__thumbs {
    display: none;
  }

  /* Inline CTA chowamy — akcję przejmuje pływający pasek na dole. */
  .qv-cta {
    display: none;
  }

  /* Pływający pasek "Dodaj do koszyka" przypięty do dołu viewportu (jak PDP). */
  .qv-mobile-cta {
    display: flex;
    gap: 8px;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
    background: var(--color-white);
    border-top: 1px solid var(--color-border);
    box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.06);
    z-index: 2;
  }

  .qv-panel {
    padding: 24px 20px 96px;
    /* Mobile: cały modal przewija się w .qv-backdrop (dialog jest wyższy niż
       viewport). Bez tego resetu panel zostaje kontenerem scrolla (overflow-y:auto
       + overscroll-behavior:contain z reguły bazowej), który nie ma czego przewijać
       (treść mieści się w auto-wierszu grida), a `contain` blokuje propagację gestu
       do backdropu → dotyk/scroll nad panelem jest „martwy" (tylko galeria, nie
       będąca kontenerem scrolla, daje się swipe'ować). overflow:visible usuwa
       kontener scrolla, więc gest dochodzi do backdropu. */
    overflow: visible;
    overscroll-behavior: auto;
  }

  /* Ten sam powód co .qv-panel — wewnętrzny scroll opisu jest wzorcem desktopowym
     (panel o stałej wysokości). Na mobile opis płynie w panelu, a przewija się
     backdrop, więc kontener scrolla opisu też trzeba zdjąć, by nie tworzył drugiej
     martwej strefy nad tekstem opisu. */
  .qv-description-body {
    overflow: visible;
    overscroll-behavior: auto;
  }

  /* Wyrównanie do góry + przewijanie backdropu: dialog (zdjęcie 187/300 + panel)
     jest wyższy niż viewport. flex-end z nieprzewijalnym overflow w osi krzyżowej
     wypychał górę dialogu (X + góra zdjęcia) ponad ekran i ucinał głowę modelki. */
  .qv-backdrop {
    align-items: flex-start;
    padding: 0;
  }

  /* X przypięty do viewportu — pozostaje dostępny po przewinięciu do panelu/CTA. */
  .qv-close {
    position: fixed;
  }

  .qv-rise-enter-from {
    transform: translateY(60px) scale(1);
  }
}
</style>
